import i18n from '../i18n.js';

const TIMEZONE = 'Asia/Jakarta';

function activeLocale(): string {
  return i18n.language === 'id' ? 'id-ID' : 'en-US';
}

export function formatCurrency(
  amount: number | string,
  isoCurrencyCode: string,
  locale?: string,
): string {
  const num = typeof amount === 'string' ? parseFloat(amount) : amount;
  const loc = locale ?? activeLocale();
  return new Intl.NumberFormat(loc, {
    style: 'currency',
    currency: isoCurrencyCode,
    minimumFractionDigits: isoCurrencyCode === 'IDR' ? 0 : 2,
    maximumFractionDigits: isoCurrencyCode === 'IDR' ? 0 : 2,
  }).format(num);
}

export function formatDate(
  d: Date | string,
  format: 'short' | 'long' | 'time' = 'short',
): string {
  const date = typeof d === 'string' ? new Date(d) : d;
  const locale = activeLocale();

  const options: Intl.DateTimeFormatOptions = { timeZone: TIMEZONE };

  if (format === 'short') {
    Object.assign(options, { year: 'numeric', month: 'short', day: 'numeric' } satisfies Intl.DateTimeFormatOptions);
  } else if (format === 'long') {
    Object.assign(options, { year: 'numeric', month: 'long', day: 'numeric', weekday: 'long' } satisfies Intl.DateTimeFormatOptions);
  } else {
    Object.assign(options, { hour: '2-digit', minute: '2-digit', timeZoneName: 'short' } satisfies Intl.DateTimeFormatOptions);
  }

  return new Intl.DateTimeFormat(locale, options).format(date);
}

const ZERO_DECIMAL_CURRENCIES = new Set(['IDR', 'VND', 'JPY', 'KRW']);

export function parseCurrencyInput(displayString: string, locale: string, currency: string): number {
  // Remove locale-specific thousand separators, then normalise decimal
  const isId = locale.startsWith('id');
  const cleaned = isId
    ? displayString.replace(/\./g, '').replace(',', '.')
    : displayString.replace(/,/g, '');
  const num = parseFloat(cleaned);
  return isNaN(num) ? 0 : ZERO_DECIMAL_CURRENCIES.has(currency) ? Math.round(num) : num;
}

export function formatNumericInput(numeric: number, locale: string, currency: string): string {
  if (!numeric) return '';
  const isId = locale.startsWith('id');
  const decimalScale = ZERO_DECIMAL_CURRENCIES.has(currency) ? 0 : 2;
  const parts = numeric.toFixed(decimalScale).split('.');
  const intPart = parts[0]!.replace(/\B(?=(\d{3})+(?!\d))/g, isId ? '.' : ',');
  const decPart = parts[1];
  if (!decPart || decimalScale === 0) return intPart;
  return `${intPart}${isId ? ',' : '.'}${decPart}`;
}

export function formatIdrAbbreviated(amount: number): string {
  if (amount >= 1_000_000_000) {
    return `IDR ${(amount / 1_000_000_000).toFixed(2)} B`;
  }
  if (amount >= 1_000_000) {
    return `IDR ${(amount / 1_000_000).toFixed(2)} M`;
  }
  return `IDR ${amount.toLocaleString('id-ID')}`;
}

export function formatRelative(d: Date | string): string {
  const date = typeof d === 'string' ? new Date(d) : d;
  const locale = activeLocale();
  const rtf = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' });

  const diffMs = date.getTime() - Date.now();
  const diffSec = Math.round(diffMs / 1000);
  const diffMin = Math.round(diffSec / 60);
  const diffHr = Math.round(diffMin / 60);
  const diffDay = Math.round(diffHr / 24);

  if (Math.abs(diffSec) < 60) return rtf.format(diffSec, 'second');
  if (Math.abs(diffMin) < 60) return rtf.format(diffMin, 'minute');
  if (Math.abs(diffHr) < 24) return rtf.format(diffHr, 'hour');
  return rtf.format(diffDay, 'day');
}
